import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const OpportunityDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (slug) {
      fetchOpportunityDetails();
    }
  }, [slug]);

  const fetchOpportunityDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const apiUrl = `${API_ENDPOINTS.OPPORTUNITIES}?slug=${encodeURIComponent(slug)}`;
      const response = await axios.get(apiUrl);

      if (response.data.success && response.data.data) {
        setOpportunity(response.data.data);
      } else {
        setError(response.data.message || 'Opportunity not found');
      }
    } catch (err) {
      console.error('Error fetching opportunity:', err);
      if (err.response) {
        if (err.response.status === 404) {
          setError('Opportunity not found');
        } else {
          setError(err.response.data?.message || 'Failed to load opportunity');
        }
      } else {
        setError('Cannot connect to the server. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'job': return { class: 'bg-success', label: 'Job' };
      case 'volunteer': return { class: 'bg-info', label: 'Volunteer' };
      case 'tender': return { class: 'bg-warning text-dark', label: 'Tender' };
      case 'internship': return { class: 'bg-secondary', label: 'Internship' };
      default: return { class: 'bg-secondary', label: type };
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading opportunity...</p>
      </div>
    );
  }

  if (error || !opportunity) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Opportunity Not Found</h4>
          <p className="mb-0">{error || 'The requested opportunity could not be found.'}</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/opportunities')}>
          <i className="bi bi-arrow-left me-2"></i> Back to Opportunities
        </button>
      </div>
    );
  }

  const badge = getTypeBadge(opportunity.type);
  const deadline = opportunity.deadline ? new Date(opportunity.deadline) : null;
  const isExpired = deadline && deadline < new Date();

  return (
    <div className="opportunity-details-page">
      {/* Hero Section */}
      <section
        className="position-relative text-white py-5"
        style={{
          background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)'
        }}
      >
        <div className="container position-relative" style={{ zIndex: 2 }}>
          <button
            className="btn btn-outline-light mb-4"
            onClick={() => navigate('/opportunities')}
          >
            <i className="bi bi-arrow-left me-2"></i> Back to Opportunities
          </button>

          <div className="mb-3">
            <span className={`badge ${badge.class} p-2`}>{badge.label}</span>
            {isExpired && <span className="badge bg-danger ms-2 p-2">Expired</span>}
          </div>

          <h1 className="display-5 fw-bold mb-3">{opportunity.title}</h1>

          <div className="d-flex flex-wrap gap-4 text-white-50">
            {opportunity.location && (
              <span>
                <i className="bi bi-geo-alt me-1"></i> {opportunity.location}
              </span>
            )}
            {opportunity.deadline && (
              <span>
                <i className="bi bi-calendar me-1"></i> Deadline: {formatDate(opportunity.deadline)}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              {opportunity.description && (
                <div className="mb-4">
                  <h5 className="mb-2">Summary</h5>
                  <p className="lead text-muted">{opportunity.description}</p>
                </div>
              )}

              {opportunity.content && (
                <div className="mb-4">
                  <h5 className="mb-3">Details</h5>
                  <div
                    className="content-html"
                    dangerouslySetInnerHTML={{ __html: opportunity.content }}
                  />
                </div>
              )}

              {opportunity.requirements && (
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body">
                    <h5 className="card-title mb-3"><i className="bi bi-list-check me-2"></i>Requirements</h5>
                    <div className="content-html" dangerouslySetInnerHTML={{ __html: opportunity.requirements }} />
                  </div>
                </div>
              )}

              {opportunity.responsibilities && (
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body">
                    <h5 className="card-title mb-3"><i className="bi bi-briefcase me-2"></i>Responsibilities</h5>
                    <div className="content-html" dangerouslySetInnerHTML={{ __html: opportunity.responsibilities }} />
                  </div>
                </div>
              )}

              {(opportunity.application_method || opportunity.application_link || opportunity.application_email || opportunity.contact_email) && (
                <div className="card border-primary mb-4">
                  <div className="card-body">
                    <h5 className="card-title mb-3"><i className="bi bi-send me-2"></i>How to Apply</h5>
                    {opportunity.application_method && (
                      <div className="content-html mb-3" dangerouslySetInnerHTML={{ __html: opportunity.application_method }} />
                    )}
                    {opportunity.application_link && (
                      <p className="mb-2">
                        <a href={opportunity.application_link} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">
                          <i className="bi bi-link me-1"></i> Apply Online
                        </a>
                      </p>
                    )}
                    {(opportunity.application_email || opportunity.contact_email) && (
                      <p className="mb-0">
                        <a href={`mailto:${opportunity.application_email || opportunity.contact_email}?subject=Application: ${encodeURIComponent(opportunity.title)}`} className="btn btn-primary">
                          <i className="bi bi-envelope me-1"></i> Email Application
                        </a>
                      </p>
                    )}
                  </div>
                </div>
              )}

              <hr className="my-5" />
              <Link to="/opportunities" className="btn btn-primary">
                <i className="bi bi-arrow-left me-2"></i> Back to All Opportunities
              </Link>
            </div>

            {/* Sidebar */}
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm sticky-top">
                <div className="card-body">
                  <h5 className="card-title mb-3">Opportunity Info</h5>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-3">
                      <strong className="text-muted d-block small">Type</strong>
                      <span className={`badge ${badge.class}`}>{badge.label}</span>
                    </li>
                    {opportunity.location && (
                      <li className="mb-3">
                        <strong className="text-muted d-block small">Location</strong>
                        {opportunity.location}
                      </li>
                    )}
                    {opportunity.deadline && (
                      <li className="mb-3">
                        <strong className="text-muted d-block small">Deadline</strong>
                        {formatDate(opportunity.deadline)}
                        {isExpired && <span className="badge bg-danger ms-2">Expired</span>}
                      </li>
                    )}
                    {opportunity.salary_range && (
                      <li className="mb-3">
                        <strong className="text-muted d-block small">Salary Range</strong>
                        {opportunity.salary_range}
                      </li>
                    )}
                    {(opportunity.contact_email || opportunity.contact_phone) && (
                      <li className="mb-0">
                        <strong className="text-muted d-block small">Contact</strong>
                        {opportunity.contact_email && (
                          <a href={`mailto:${opportunity.contact_email}`}>{opportunity.contact_email}</a>
                        )}
                        {opportunity.contact_phone && (
                          <span className="d-block"><a href={`tel:${opportunity.contact_phone}`}>{opportunity.contact_phone}</a></span>
                        )}
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OpportunityDetails;
